import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gold/60 bg-navy text-ivory shadow-lg shadow-navy/30 transition-colors duration-150 ease-out hover:bg-navy-light focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50';
  return <section className="bg-ivory py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-101" data-mp="1wd6ces28c605">
      <div className="mx-auto max-w-6xl px-4" data-id="element-102" data-mp="3vnxrq1dhxqm6">
        <div className="text-center" data-id="element-103" data-mp="3vmmoq3rvkmw4">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-gold-dark" data-id="element-104" data-mp="3qpnw5pnv0o01">Guarda dentro</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-4xl" data-id="element-105" data-mp="1p0gpt902mu7x">
            Scopri cosa c&apos;è dentro…
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-106" data-mp="1f458u5pjdg75">
            <span className="h-1 w-10 rounded-full bg-gold" data-id="element-107" data-mp="1k2f5ylutmqve" />
            <span className="h-1 w-3 rounded-full bg-navy/30" data-id="element-108" data-mp="27rhl9n1zg7mq" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-navy/75" data-id="element-109" data-mp="27pesiv9g40eb">
            Pagine visive con infografiche, schemi chiari, concetti chiave, errori comuni degli studenti e attività pronte per la
            classe. Ecco alcune pagine reali del Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carosello" aria-label="Pagine di esempio" data-id="element-110" data-mp="22tzlon9uv4re">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-111" data-mp="rcq3s56dkks7">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border-2 bg-white shadow-lg shadow-navy/20 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-gold opacity-100' : 'border-ivory-dark opacity-60'}`} aria-roledescription="slide" aria-label={`${index + 1} di ${total}`} data-id="element-112" data-mp="trmque0wp00m" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-113" data-mp="3s75xni8evm79" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClass} left-0 sm:-left-2`} aria-label="Pagina precedente" data-id="element-114" data-mp="60r6g1ys573e">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-115" data-mp="39jao6aju0ujy" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClass} right-0 sm:-right-2`} aria-label="Pagina successiva" data-id="element-116" data-mp="2crdv1au2s9gj">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-117" data-mp="2gtshe4wydrck" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-118" data-mp="2lqivimp76cvc">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-ivory ${index === active ? 'w-6 bg-gold' : 'w-2 bg-navy/25 hover:bg-navy/40'}`} aria-label={`Vai alla pagina ${index + 1}`} aria-current={index === active} data-id="element-119" data-mp="2qjkcd08ysmhk" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}